import { Draft, PayloadAction } from "@reduxjs/toolkit";

import { AutocompleteItem } from "../../../../components/Autocomplete";
import { FormId } from "../../../../components/enums";
import { defaultCountry } from "../../../../shared";
import {
  createGenericUISlice,
  GenericState,
  UIState,
} from "../../../../store/HOC/genericUI.slice";
import { generateFormattedDate } from "../../../../utilities/functions";
import { ComponentId as ApplicantComponentId } from "../Introductory/ApplicantData/enums";
import { FormDataInputs as IntroductoryFormDataInputs } from "../Introductory/introductory.slice";

import { ComponentId as CircumstancesComponentId } from "./CircumstancesData/enums";
import { ComponentId as DriverComponentId } from "./DriverData/enums";
import { ComponentId as OtherPersonsComponentId } from "./OtherPersonsData/enums";
import { ComponentId as StatementsComponentId } from "./StatementsData/enums";
import { ComponentId as WitnessesComponentId } from "./WitnessesData/enums";

export type FormDataInputs = {
  [FormId.INCIDENT_CIRCUMSTANCES]: {
    [CircumstancesComponentId.CIRCUMSTANCES_DATE]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_HOUR]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_PLACE]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_CITY]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_ZIP_CODE_PL]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_ZIP_CODE_GENERIC]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_COUNTRY]: AutocompleteItem;
    [CircumstancesComponentId.CIRCUMSTANCES_PROVINCE]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_WAS_VEHICLE_IN_MOTION]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_DESCRIPTION]: string;
    [CircumstancesComponentId.CIRCUMSTANCES_WAS_DRIVER]: string;
  };
  [FormId.INCIDENT_DRIVER]: {
    [DriverComponentId.DRIVER_NAME]: string;
    [DriverComponentId.DRIVER_SURNAME]: string;
    [DriverComponentId.DRIVER_PHONE]: string;
    [DriverComponentId.DRIVER_PESEL]: string;
    [DriverComponentId.DRIVER_LACK_OF_PESEL]: string;
    [DriverComponentId.DRIVER_OTHER_ID]: string;
    [DriverComponentId.DRIVER_OTHER_ID_NUMBER]: string;
    [DriverComponentId.DRIVER_STREET]: string;
    [DriverComponentId.DRIVER_STREET_2]: string;
    [DriverComponentId.DRIVER_ZIP_CODE]: string;
    [DriverComponentId.DRIVER_CITY]: string;
    [DriverComponentId.DRIVER_PROVINCE]: string;
  };
  [FormId.INCIDENT_STATEMENTS]: {
    [StatementsComponentId.INCIDENT_STATEMENTS_TYPE]: string;
    [StatementsComponentId.INCIDENT_STATEMENTS_POLICE_DEPARTMENT]: string;
    [StatementsComponentId.INCIDENT_STATEMENTS_CASE_NUMBER]: string;
  };
  [FormId.INCIDENT_OTHER_PERSONS]: {
    [OtherPersonsComponentId.NUMBER_OF_PARTICIPANTS]: string;
    [OtherPersonsComponentId.ARE_INJURED_PERSONS]: string;
    [OtherPersonsComponentId.GOT_INJURED_PERSONS_DETAILS]: string;
    [OtherPersonsComponentId.PERSONS]: {
      [key: string]: { [key: string]: string };
    };
  };
  [FormId.INCIDENT_WITNESSES]: {
    [WitnessesComponentId.IS_WITNESSES_DATA_AVAILABLE]: string;
    [WitnessesComponentId.PERSONS]: {
      [key: string]: { [key: string]: string };
    };
  };
};

export type IncidentCircumstancesFormInputs = FormDataInputs[FormId.INCIDENT_CIRCUMSTANCES];
export type IncidentDriverFormInputs = FormDataInputs[FormId.INCIDENT_DRIVER];
export type IncidentStatementsFormInputs = FormDataInputs[FormId.INCIDENT_STATEMENTS];
export type IncidentOtherPersonsFormInputs = FormDataInputs[FormId.INCIDENT_OTHER_PERSONS];
export type IncidentWitnessesFormInputs = FormDataInputs[FormId.INCIDENT_WITNESSES];

type IncidentState = {
  data: FormDataInputs;
  ui: UIState;
};

const initialState: IncidentState = {
  data: {
    [FormId.INCIDENT_CIRCUMSTANCES]: {
      [CircumstancesComponentId.CIRCUMSTANCES_DATE]: generateFormattedDate(),
      [CircumstancesComponentId.CIRCUMSTANCES_HOUR]: "",
      [CircumstancesComponentId.CIRCUMSTANCES_PLACE]: "",
      [CircumstancesComponentId.CIRCUMSTANCES_CITY]: "",
      [CircumstancesComponentId.CIRCUMSTANCES_ZIP_CODE_PL]: "",
      [CircumstancesComponentId.CIRCUMSTANCES_ZIP_CODE_GENERIC]: "",
      [CircumstancesComponentId.CIRCUMSTANCES_COUNTRY]: {
        ...defaultCountry,
      },
      [CircumstancesComponentId.CIRCUMSTANCES_PROVINCE]: "",
      [CircumstancesComponentId.CIRCUMSTANCES_WAS_VEHICLE_IN_MOTION]: "",
      [CircumstancesComponentId.CIRCUMSTANCES_DESCRIPTION]: "",
      [CircumstancesComponentId.CIRCUMSTANCES_WAS_DRIVER]: "",
    },
    [FormId.INCIDENT_DRIVER]: {
      [DriverComponentId.DRIVER_NAME]: "",
      [DriverComponentId.DRIVER_SURNAME]: "",
      [DriverComponentId.DRIVER_PHONE]: "",
      [DriverComponentId.DRIVER_PESEL]: "",
      [DriverComponentId.DRIVER_LACK_OF_PESEL]: "",
      [DriverComponentId.DRIVER_OTHER_ID]: "",
      [DriverComponentId.DRIVER_OTHER_ID_NUMBER]: "",
      [DriverComponentId.DRIVER_STREET]: "",
      [DriverComponentId.DRIVER_STREET_2]: "",
      [DriverComponentId.DRIVER_ZIP_CODE]: "",
      [DriverComponentId.DRIVER_CITY]: "",
      [DriverComponentId.DRIVER_PROVINCE]: "",
    },
    [FormId.INCIDENT_STATEMENTS]: {
      [StatementsComponentId.INCIDENT_STATEMENTS_TYPE]: "",
      [StatementsComponentId.INCIDENT_STATEMENTS_POLICE_DEPARTMENT]: "",
      [StatementsComponentId.INCIDENT_STATEMENTS_CASE_NUMBER]: "",
    },
    [FormId.INCIDENT_OTHER_PERSONS]: {
      [OtherPersonsComponentId.NUMBER_OF_PARTICIPANTS]: "",
      [OtherPersonsComponentId.ARE_INJURED_PERSONS]: "",
      [OtherPersonsComponentId.GOT_INJURED_PERSONS_DETAILS]: "",
      [OtherPersonsComponentId.PERSONS]: {},
    },
    [FormId.INCIDENT_WITNESSES]: {
      [WitnessesComponentId.IS_WITNESSES_DATA_AVAILABLE]: "",
      [WitnessesComponentId.PERSONS]: {},
    },
  },
  ui: {
    validated: false,
    "circumstances[country]": defaultCountry.value,
  },
};

const incidentSlice = createGenericUISlice({
  name: "@OC/vehicle/incident",
  initialState: initialState as GenericState<FormDataInputs>,
  reducers: {
    setData: (state, action: PayloadAction<FormDataInputs>) => {
      state.data[FormId.INCIDENT_CIRCUMSTANCES] = {
        ...initialState.data[FormId.INCIDENT_CIRCUMSTANCES],
        ...action.payload[FormId.INCIDENT_CIRCUMSTANCES],
      };
      state.data[FormId.INCIDENT_DRIVER] = {
        ...initialState.data[FormId.INCIDENT_DRIVER],
        ...action.payload[FormId.INCIDENT_DRIVER],
      };
      state.data[FormId.INCIDENT_STATEMENTS] = {
        ...initialState.data[FormId.INCIDENT_STATEMENTS],
        ...action.payload[FormId.INCIDENT_STATEMENTS],
      };
      state.data[FormId.INCIDENT_OTHER_PERSONS] = {
        ...initialState.data[FormId.INCIDENT_OTHER_PERSONS],
        ...action.payload[FormId.INCIDENT_OTHER_PERSONS],
      };
      state.data[FormId.INCIDENT_WITNESSES] = {
        ...initialState.data[FormId.INCIDENT_WITNESSES],
        ...action.payload[FormId.INCIDENT_WITNESSES],
      };
      state.ui.validated = true;
    },
    setWasVehicleInMotion: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_CIRCUMSTANCES][
        CircumstancesComponentId.CIRCUMSTANCES_WAS_VEHICLE_IN_MOTION
      ] = action.payload;
      state.data[FormId.INCIDENT_CIRCUMSTANCES][
        CircumstancesComponentId.CIRCUMSTANCES_DESCRIPTION
      ] =
        initialState.data[FormId.INCIDENT_CIRCUMSTANCES][
          CircumstancesComponentId.CIRCUMSTANCES_DESCRIPTION
        ];
    },

    setWasDriver: (
      state,
      action: PayloadAction<
        Pick<
          IntroductoryFormDataInputs[FormId.INTRODUCTORY_APPLICANT],
          | ApplicantComponentId.APPLICANT_NAME
          | ApplicantComponentId.APPLICANT_SURNAME
          | ApplicantComponentId.APPLICANT_PHONE
        > & {
          countryValidator: string;
          [CircumstancesComponentId.CIRCUMSTANCES_WAS_DRIVER]: string;
        }
      >
    ) => {
      const {
        payload: { countryValidator, driver, ...restPayload },
      } = action;

      state.data[FormId.INCIDENT_CIRCUMSTANCES][
        CircumstancesComponentId.CIRCUMSTANCES_WAS_DRIVER
      ] = driver;

      if (driver === "true") {
        state.data[FormId.INCIDENT_DRIVER] = {
          ...state.data[FormId.INCIDENT_DRIVER],
          ...restPayload,
        };
      }

      if (!("driverphone" in state.ui)) {
        state.ui.driverphone = {
          isActive: true,
        };
      }

      state.ui.driverphone = {
        ...(state.ui.driverphone as Draft<Record<string, unknown>>),
        country: countryValidator,
      };
    },
    setLackOfPesel: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_DRIVER][
        DriverComponentId.DRIVER_LACK_OF_PESEL
      ] = action.payload;
      state.ui.driverLackOfPesel = action.payload;
    },
    setStatementType: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_STATEMENTS][
        StatementsComponentId.INCIDENT_STATEMENTS_TYPE
      ] = action.payload;
    },
    setAreInjuredPersons: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_OTHER_PERSONS][
        OtherPersonsComponentId.ARE_INJURED_PERSONS
      ] = action.payload;
      state.data[FormId.INCIDENT_OTHER_PERSONS][
        OtherPersonsComponentId.GOT_INJURED_PERSONS_DETAILS
      ] =
        initialState.data[FormId.INCIDENT_OTHER_PERSONS][
          OtherPersonsComponentId.GOT_INJURED_PERSONS_DETAILS
        ];
    },
    setAvailableCities: (
      state,
      action: PayloadAction<{ label: string; value: string }[]>
    ) => {
      state.ui["circumAvailableCities"] = action.payload;
    },
    setGotInjuredPersonsDetails: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_OTHER_PERSONS][
        OtherPersonsComponentId.GOT_INJURED_PERSONS_DETAILS
      ] = action.payload;
      state.data[FormId.INCIDENT_OTHER_PERSONS][
        OtherPersonsComponentId.PERSONS
      ] =
        initialState.data[FormId.INCIDENT_OTHER_PERSONS][
          OtherPersonsComponentId.PERSONS
        ];
    },
    setIsWitnessesDataAvailable: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_WITNESSES][
        WitnessesComponentId.IS_WITNESSES_DATA_AVAILABLE
      ] = action.payload;
      state.data[FormId.INCIDENT_WITNESSES][WitnessesComponentId.PERSONS] =
        initialState.data[FormId.INCIDENT_WITNESSES][
          WitnessesComponentId.PERSONS
        ];
    },
  },
});

export const {
  setData: setVehicleIncidentFormData,
  setLackOfPesel,
  setStatementType,
  setWasDriver,
  setWasVehicleInMotion,
  setAreInjuredPersons,
  setGotInjuredPersonsDetails,
  setIsWitnessesDataAvailable,
  setUIField,
  setUIObjectField,
  setAvailableCities,
} = incidentSlice.actions;

export const { reducer } = incidentSlice;
